import { BREAKPOINTS, TextButton as Button, COLORS, HyperLink, SystemIcons } from '@laerdal/life-react-components';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageWidthFull } from './PageWidthFull';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectMarket, buildMarketPath } from '../store/slices/globalConfigSlice';
import { useState, useEffect, useRef, useMemo } from 'react';
import posthog from 'posthog-js';
import { useMediaMatch } from 'rooks';


const FullPageContainer = styled.div`
  display:flex;
  background: ${COLORS.neutral_20};
`;

const DownloadLink = styled(HyperLink)`
    font-size: 20px;

    svg {
      padding-bottom: 7px;
      vertical-align: middle;
    }
`;

const Breadcrumb = styled.div`
  padding: 12px 0 14px;
  margin-top: 32px;
`;
const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  margin-bottom: 32px;
  align-items: flex-start;
`;
const ContentColumn = styled.div`
  display:flex;
  flex-direction:column;
  gap: 16px;
  flex: 1;
  align-items: start;
`;
const ImageColumn = styled.div`
  display: none;
  ${BREAKPOINTS.MEDIUM}{
    display:flex;
  }
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  img { 
    width: 256px;
    height: 160px;
    border-radius: 4px;
    object-fit: contain;
    object-position: left center;
  }
`;
const Title = styled.div`
  color: #1F1F1F;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  ${BREAKPOINTS.MEDIUM}{
    color: var(--life-color-text-default);
    font-size: 40px;
  }
`;
const Description = styled.div`
  color: #1F1F1F;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  ${BREAKPOINTS.MEDIUM}{
    color: var(--life-color-text-default);
    font-size: 20px;
  }
`;

const ReadMoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${COLORS.primary_600};
  font-family: Lato;
  font-size: inherit;
  font-weight: 700;
  line-height: inherit;
`;

const TabsBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  flex-direction: row;
  gap: 0;
  background: ${COLORS.neutral_20};
  border-bottom: 1px solid #CCC;
  /* cancel the page wrapper gap so the bar joins the hero section */
  margin-top: -32px;
  /* mobile: horizontally scrollable */
  justify-content: flex-start;
  overflow-x: auto;
  padding: 0 16px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${BREAKPOINTS.MEDIUM}{
    margin-top: -128px;
    /* first section sits 64px below the tabs while other sections keep the 128px gap */
    margin-bottom: -64px;
    justify-content: center;
    overflow-x: visible;
    padding: 0;
  }
`;

const Tab = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  height: 56.001px;
  min-width: 64px;
  flex-shrink: 0;
  white-space: nowrap;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
  border: none;
  cursor: pointer;
  color: var(--life-color-text-subtle);
  text-align: center;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  background: ${({ $selected }) => ($selected ? COLORS.neutral_50 : 'none')};

  &:hover {
    background: #F1FBFE;
  }

  &::after {
    content: '';
    /* underline extends 16px beyond the text on each side */
    width: calc(100% + 32px);
    height: 4px;
    border-radius: var(--life-radius-full);
    background: ${({ $selected }) => ($selected ? 'var(--life-color-border-selected)' : 'transparent')};
  }
`;


const CrumbLink = styled.a`
  &:link, &:visited, &:hover, &:active {
    color: ${COLORS.primary_600};
    text-decoration: none; 
  }

  color: ${COLORS.primary_600};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px; 
`;

const CrumbText = styled.span`
  color: ${COLORS.neutral_500};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px; 
`;

const Devider = styled.span`
  padding: 0 10px;
  color: ${COLORS.neutral_500};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px; 
`;

interface Props {
  title: string,
  description: string,
  asset: string,
  buttonText?: string,
  buttonLink?: string,
  paligoUserGuideUrl?: string,
  breadcrumb: Crumb[],
  hasDownloads?: boolean,
  productLineCode?: string,
  tabs?: { id: string, label: string }[],
  activeTab?: string,
  onTabClick?: (id: string) => void
}
interface Crumb {
  text: string;
  link?: string;
}

const ProductTopSection = (props: Props) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate()
  const currentMartket = useSelector(selectMarket);
  const [downloadLink, setDownloadLink] = useState<string>('');
  const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(false);
  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
  const isMobile = !isMediumScreen;

  // strip tags, then decode entities (&reg; etc.) since the preview renders as plain text
  const plainDescription = useMemo(() => {
    const stripped = props.description?.replace(/<[^>]*>/g, '') ?? '';
    const el = document.createElement('textarea');
    el.innerHTML = stripped;
    return el.value;
  }, [props.description]);
  const isLongDescription = plainDescription.length > 230;

  const tabsBarRef = useRef<HTMLDivElement>(null);

  // keep the highlighted tab visible when the bar is horizontally scrollable
  useEffect(() => {
    const bar = tabsBarRef.current;
    if (!props.activeTab || !bar || bar.scrollWidth <= bar.clientWidth) return;
    const tab = bar.querySelector<HTMLElement>(`[data-tab-id="${props.activeTab}"]`);
    if (!tab) return;
    bar.scrollTo({
      left: tab.offsetLeft - (bar.clientWidth - tab.clientWidth) / 2,
      behavior: 'smooth',
    });
  }, [props.activeTab]);


  useEffect(() => {
    if (props.hasDownloads && props.productLineCode && process.env.REACT_APP_DOWNLOAD_LINK?.toString() === '/downloads') {
      setDownloadLink(currentMartket ?
        `/${currentMartket}/downloads/${props.productLineCode}`
        : `/downloads/${props.productLineCode}`);
    }
    else
      setDownloadLink('');

  }, [currentMartket, props.hasDownloads, props.productLineCode]);



  return (
    <>
    <FullPageContainer>
      <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
        <Breadcrumb>
          {props.breadcrumb.map((item, index) =>
            <>
              {!!item.link &&
                <CrumbLink
                  key={`breadcrumb-href-${index}`}
                  href={item.link}
                  onClick={(e) => {
                    e.preventDefault();
                    posthog.capture('SUPP-BreadcrumbClick', {
                      currentUrl: window.location.href,
                      clickedLink: item.link,
                      step: 1
                    });
                    navigate(item.link!);
                  }}
                >
                  {item.text}
                </CrumbLink>
              }
              {!item.link &&
                <CrumbText
                  key={`breadcrumb-text-${index}`}>
                  {item.text}
                </CrumbText>
              }
              {(props.breadcrumb.length != index + 1) &&
                <Devider
                  key={`breadcrumb-devider-${index}`}>/</Devider>
              }
            </>
          )
          }
        </Breadcrumb>
        <ColumnContainer>
          {!!props.asset && <ImageColumn>
            <img src={props.asset} alt={props.title} />
          </ImageColumn>}
          <ContentColumn>
            <Title>{props.title}</Title>
            <Description>
              {(!isLongDescription || descriptionExpanded)
                ? <span dangerouslySetInnerHTML={{ __html: props.description }} />
                : <span>{`${plainDescription.slice(0, 230).trimEnd()}… `}</span>}
              {isLongDescription &&
                <ReadMoreLink onClick={() => setDescriptionExpanded(!descriptionExpanded)}>
                  {descriptionExpanded ? t('Read less') : t('Read more')}
                </ReadMoreLink>}
            </Description>
            {!!props.hasDownloads && props.productLineCode && !!downloadLink &&
              <>
                <DownloadLink
                  href={downloadLink}
                  variant='default'
                  onClick={(e) => {
                    e.preventDefault()
                    // @ts-ignore
                    posthog.capture('SUPP-ProductPageDownloadLink', {
                      title: props.title,
                      currentUrl: window.location.href,
                      targetUrl: downloadLink,
                    });
                    navigate(`${window.location.pathname}/downloads`);
                  }}
                >
                  {t('Downloads')} <SystemIcons.OpenNewWindow size='28px' />
                </DownloadLink>
              </>
            }
            {props.productLineCode == 'qcpr-app' && isMobile &&
              <Button
                variant='secondary'
                onClick={() => {
                  // @ts-ignore
                  posthog.capture('SUPP-ProductTopSection-TroubleshootingGuideClick', {
                    title: props.title,
                    productLineCode: props.productLineCode,
                  });
                  navigate(buildMarketPath(currentMartket, '/qcpr-support/troubleshooting'))
                }}
              >
                {t('Troubleshooting Guide')}
              </Button>}
          </ContentColumn>
        </ColumnContainer>
      </PageWidthFull>

    </FullPageContainer>
    {!!props.tabs?.length &&
      <TabsBar ref={tabsBarRef}>
        {props.tabs.map((tab) =>
          <Tab
            key={tab.id}
            data-tab-id={tab.id}
            $selected={props.activeTab === tab.id}
            onClick={() => props.onTabClick?.(tab.id)}
          >
            {tab.label}
          </Tab>
        )}
      </TabsBar>
    }
    </>
  );
}

export default ProductTopSection;